import { Component, Input, OnInit, Renderer2 } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { UntypedFormGroup } from "@angular/forms";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { DetailAktDataRetrieveResponse } from "../../../api/models/detail-akt-data-retrieve-response";
import { DokumentTyp } from "../file-overview/file-overview.component";
import { OdataDokumentResponse } from "../../../api/models/odata-dokument-response";
import { DokumenteService } from "../../../api/services/dokumente.service";
import {
  ComposeEmailComponent,
  EmailRecipient,
  MailData,
} from "../compose-email/compose-email.component";
import { TermineService } from "../../../api/services/termine.service";
import {
  EmpfaengerGruppe,
  TerminRueckfrageAnStatus,
} from "../../enums/global-enums";
import { ParsedEmlJson } from "eml-parse-js";
import DOMPurify from "dompurify";
import { EmailService } from "../../services/email.service";

@Component({
  selector: "app-rueckfrage-an-email-versenden",
  templateUrl: "./rueckfrage-an-email.component.html",
  styleUrls: ["./rueckfrage-an-email.component.scss"],
  standalone: false,
})
export class RueckfrageAnEmailComponent implements OnInit {
  @Input({ required: true }) aktId: number;
  @Input({ required: true }) aktTitle: number;
  @Input({ required: true }) terminBearbeitenFormGroup: UntypedFormGroup;
  @Input({ required: true }) terminId: number;
  @Input({ required: true }) details: DetailAktDataRetrieveResponse;
  @Input({ required: true }) terminDateValue: Date;
  @Input({ required: true }) terminTimeValue: string;
  @Input() grund: string;

  odataAktDokumente: OdataDokumentResponse[];
  odataAktDokumenteFetchFailed: boolean = false;

  constructor(
    private modalController: ModalController,
    private oidcSecurityService: OidcSecurityService,
    private dokumenteService: DokumenteService,
    private termineService: TermineService,
    private emailService: EmailService,
    protected renderer: Renderer2,
  ) {}

  ngOnInit() {
    this.fetchDokuments();
  }

  cancelButton() {
    this.modalController.dismiss(null, "cancel");
  }

  fetchDokuments() {
    this.odataAktDokumente = null;
    this.dokumenteService
      .dokumenteOdataAktCreate({
        token: this.oidcSecurityService.getToken(),
        akt_id: this.aktId,
      })
      .subscribe({
        next: (result: OdataDokumentResponse[]) => {
          this.odataAktDokumente = result.filter(
            (entry) =>
              entry.datei_name.endsWith(".pms") ||
              entry.datei_name.endsWith(".eml"),
          );
        },
        error: (error) => {
          this.odataAktDokumenteFetchFailed = true;
        },
      });
  }

  async openEmailVersendenModal(email: ParsedEmlJson | any) {
    const existingSubject = email?.subject ?? "";
    const finalDefaultSubject = this.emailService.getDefaultEmailSubject(
      this.details,
      new Date(),
      existingSubject,
      " Re: ",
    );

    const additionalDefaultRecipients: EmailRecipient[] = [];
    const emailRegex = this.emailService.getEmailValidationRegex();
    const isValidEmail = emailRegex.test(email?.from?.email);
    if (isValidEmail) {
      additionalDefaultRecipients.push({
        empfaengerGruppe: EmpfaengerGruppe.to,
        name: email?.from?.name,
        emailAddress: email?.from?.email,
        required: false,
        person: null,
      });
    }
    const date = email?.date;

    const formattedDate = new Intl.DateTimeFormat("de-DE", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
    const responseDivider =
      "\n\n-----Ursprüngliche Nachricht-----\n" +
      `Von: ${email?.from?.email ?? ""}\n` +
      `Gesendet: ${formattedDate}\n` +
      `An: ${
        email?.to?.length
          ? email?.to?.map((entry) => entry?.email)?.join("; ")
          : email?.to?.email ?? ""
      }\n` +
      `Cc: ${
        email?.cc?.length
          ? email?.cc?.map((entry) => entry?.email)?.join("; ")
          : email?.cc?.email ?? ""
      }\n` +
      `Betreff: ${email?.subject ?? ""}\n\n`;

    const parser = new DOMParser();
    const doc = parser.parseFromString(email?.html, "text/html");

    const bodyContent = DOMPurify.sanitize(doc.body.innerHTML);
    const mailText = email?.text ?? bodyContent ?? "";
    const defaultMailBody = (responseDivider + mailText)
      .replace(/\r/g, "")
      .replace(/\n/g, "<br>");

    const requiredDefaultToBackofficeStatus: TerminRueckfrageAnStatus[] = [
      TerminRueckfrageAnStatus.UeberBOVersendet,
    ];
    const useBetreffAsGrundOnStatus: TerminRueckfrageAnStatus[] = [
      TerminRueckfrageAnStatus.Abgeschlossen,
      TerminRueckfrageAnStatus.WeitereRecherche,
    ];

    const modal = await this.modalController.create({
      component: ComposeEmailComponent,
      cssClass: ["bigger-modal"],
      backdropDismiss: false,
      componentProps: {
        aktId: this.aktId,
        aktTitle: this.aktTitle,
        defaultSubject: finalDefaultSubject,
        setRecipientToBackoffice: requiredDefaultToBackofficeStatus.includes(
          this.terminBearbeitenFormGroup?.get("TerminStatus")?.value,
        ),
        terminBestaetigenFormGroup: this.terminBearbeitenFormGroup,
        terminStatus:
          this.terminBearbeitenFormGroup?.get("TerminStatus")?.value,
        date: this.terminDateValue,
        formattedTime: this.terminTimeValue,
        terminId: this.terminId,
        details: this.details,
        submitMessage:
          "Der Rückfragetermin wird bearbeitet und das E-Mail wird versendet.",
        includeVUSBRecipient: false,
        additionalDefaultRecipients: requiredDefaultToBackofficeStatus.includes(
          this.terminBearbeitenFormGroup?.get("TerminStatus")?.value,
        )
          ? []
          : additionalDefaultRecipients,
        defaultMailBody: defaultMailBody,
        denyAdditionalRecipients: requiredDefaultToBackofficeStatus.includes(
          this.terminBearbeitenFormGroup?.get("TerminStatus")?.value,
        ),
        callAfterSubmit: (mail: MailData) => {
          return new Promise<void>((resolve, reject) => {
            this.termineService
              .termineOdataRueckfranBearbeitenCreate({
                akt_id: this.aktId,
                token: this.oidcSecurityService.getToken(),
                BemerkungAnBO:
                  this.terminBearbeitenFormGroup?.get("BemerkungNeu")?.value,
                Grund: useBetreffAsGrundOnStatus.includes(
                  this.terminBearbeitenFormGroup?.get("TerminStatus")?.value,
                )
                  ? this.terminBearbeitenFormGroup?.get("BetreffNeu")?.value
                  : this.grund,
                RueckfrageAnStatus:
                  this.terminBearbeitenFormGroup?.get("TerminStatus")?.value,
                TerminId: this.terminId,
                to: mail.to,
                cc: mail.cc,
                bcc: mail.bcc,
                subject: mail.subject,
                message: mail.message,
                attachments: mail.attachments,
                date: mail.date,
              })
              .subscribe({
                next: (result) => {
                  resolve();
                },
                error: async (error) => {
                  const message = `Fehler beim Bearbeiten der Rückfrage!`;
                  reject(new Error(message));
                },
              });
          });
        },
      },
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role == "confirm") {
      try {
        let topModal = await this.modalController.getTop();
        while (topModal) {
          await this.modalController.dismiss(null, "confirm");
          topModal = await this.modalController.getTop();
        }
      } catch (error) {
        console.error("Error dismissing modals:", error);
      }
    }
  }

  onEmailSelected(emlJson: ParsedEmlJson) {
    this.openEmailVersendenModal(emlJson);
  }

  protected readonly DokumentTyp = DokumentTyp;
}
